import { ungzip } from 'pako';

import { UprDownloadResult } from '~/store/api/types/uprDocuments';

const PDF_MAGIC = '%PDF-';

/**
 * Upper bound on decompressed PDF size. Guards against gzip "bombs"
 * while staying well above the size of legitimately large clinical PDFs.
 */
const MAX_DECOMPRESSED_BYTES = 500 * 1024 * 1024; // 500 MB

export class UprDocumentParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UprDocumentParseError';
  }
}

function base64ToUint8Array(b64: string): Uint8Array {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Converts a `gzippedFile` value into raw gzip bytes. The value may be a base64
 * string, a byte array, or a serialized Node Buffer (`{ type: 'Buffer', data:
 * number[] }`). For the Buffer shape the inner `data` array is used. Returns
 * `null` when no gzip bytes can be extracted.
 */
function gzippedFileToUint8Array(gzippedFile: unknown): Uint8Array | null {
  const value =
    gzippedFile && typeof gzippedFile === 'object' && !Array.isArray(gzippedFile)
      ? (gzippedFile as { data?: unknown }).data
      : gzippedFile;

  if (typeof value === 'string') {
    return base64ToUint8Array(value);
  }
  if (Array.isArray(value)) {
    return Uint8Array.from(value as number[]);
  }
  return null;
}

function assertPdfMagic(bytes: Uint8Array): void {
  if (bytes.length < 5) {
    throw new UprDocumentParseError('Invalid PDF data');
  }
  const head = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3], bytes[4]);
  if (head !== PDF_MAGIC) {
    throw new UprDocumentParseError('Invalid PDF data');
  }
}

export function decodeGzipToPdfBytes(compressed: Uint8Array): Uint8Array {
  try {
    const out = ungzip(compressed);
    if (out.length > MAX_DECOMPRESSED_BYTES) {
      throw new UprDocumentParseError('Document is too large');
    }
    assertPdfMagic(out);
    return out;
  } catch (e) {
    if (e instanceof UprDocumentParseError) {
      throw e;
    }
    throw new UprDocumentParseError('Could not decompress document');
  }
}

export function parseUprDownloadJsonResponse(data: unknown): UprDownloadResult {
  // The response may arrive as an already-parsed object or, when the backend
  // sends a non-JSON content type (e.g. application/octet-stream), as a raw
  // JSON string that axios did not auto-parse.
  let payload = data;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      throw new UprDocumentParseError('Invalid document response');
    }
  }
  if (!payload || typeof payload !== 'object') {
    throw new UprDocumentParseError('Invalid document response');
  }
  const o = payload as Record<string, unknown>;
  const compressed = gzippedFileToUint8Array(o.gzippedFile);
  if (!compressed) {
    throw new UprDocumentParseError('Missing gzippedFile');
  }
  const pdfBytes = decodeGzipToPdfBytes(compressed);
  return {
    pdfBytes,
    fileName: typeof o.fileName === 'string' ? o.fileName : undefined,
    id: typeof o.id === 'string' ? o.id : undefined,
    documentCreatedAt: typeof o.documentCreatedAt === 'string' ? o.documentCreatedAt : undefined,
    documentType: typeof o.documentType === 'string' ? o.documentType : undefined,
  };
}
